import React, {useEffect, useState} from 'react';
import {IonToast, isPlatform, useIonLoading} from '@ionic/react';
import {useForm} from 'react-hook-form';
import {FORM_FIELDS, FormFieldsType} from './loginForm.types';
import Form from '../../../components/form'
import {useHistory, useLocation} from 'react-router-dom';
import {stripServiceSuffix, validateEmail} from '../../../services/validation.service';
import {
    isEmployed,
    login,
    logout,
    storeProfile,
    storeToken,
    useAuth
} from '../../../services/auth.service';
import {getProfile, getRedirectAfterLogin} from '../../../services/profile.service';
import {AuthResult} from '../../../types/auth-result';
import {useTranslation} from 'react-i18next';
import {push} from "../../../services/push.service";
import {Links} from "../../links";
import AppApprovalsModal from "../../mainPage/tabs/settings/modals/appApprovalsModal.component";
import TeamMemberPhoneModal from "../../../modals/teamMemberPhone/teamMemberPhone.component";
import {Preferences} from "@capacitor/preferences";
import {RecruitmentPathTestVersion} from "../../../enums/recruitmentPathTestVersion";
import ExactLoginWhite from "@assets/images/exact-logo-white.svg";
import greenCheck from "@assets/images/check-green.svg";
import {StyledInput, StyledInputEmail} from "@components/form/input/input.style";
import {SecureStoragePlugin} from "capacitor-secure-storage-plugin";
import PinModal, {PinModalConfiguration} from "@modals/sign/pin.component";
import {useEvent} from "@hooks/useEvent";
import {EventType} from "@enums/eventType";
import {getLoginMethod} from '../../../services/loginMethod.service';
import {getMsToken, getMsTokenWeb, msConnect, msLogout} from '../../../services/msAuth.service';
import EditImage from '@assets/images/pen-edit.svg';

type LoginStep = 'email' | 'checking' | 'password' | 'microsoft';

interface LocationState {
    toastInit: string;
    showToastInit: boolean;
}

const LoginForm: React.FC = () => {
    const auth = useAuth();
    const {register, handleSubmit, watch, errors, formState, setValue} = useForm({
        mode: 'onBlur'
    });
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const {dispatch} = useEvent();

    const watchEmail = watch(FORM_FIELDS.EMAIL, '');
    const watchPassword = watch(FORM_FIELDS.PASSWORD, '');

    const [loginStep, setLoginStep] = useState<LoginStep>('email');

    const location = useLocation<LocationState>();
    const {toastInit, showToastInit} = location.state || {toastInit: "", showToastInit: false};

    const [showToast, setShowToast] = useState(showToastInit);
    const [toast, setToast] = useState<any>(toastInit);
    const [showApprovals, updateShowApprovals] = useState<boolean>(false);
    const [showTeamMemberPhone, updateShowTeamMemberPhone] = useState<boolean>(false);
    const [countFailLogin, updateCountFailLogin] = useState<number>(0);
    const [presentLoading, dismissLoading] = useIonLoading();
    const [showPinLoginModal, setShowPinLoginModal] = useState<boolean>(false);
    const [pinModalConfiguration, setPinModalConfiguration] = useState<PinModalConfiguration|null>();
    const [showMsInfoToast, setShowMsInfoToast] = useState(false);

    let invalidEmailError = t("login.invalidEmail");

    const setLastLoginEmail = async () => {
        let email = await Preferences.get({'key': 'last_login_email'});
        if (email && email.value) {
            setValue(FORM_FIELDS.EMAIL, email.value);

            let pinLogin = await SecureStoragePlugin.get({'key': 'pin_login_enabled'})
            let pinLoginAvailable = await SecureStoragePlugin.get({'key': 'pin_login_available'});

            if (pinLogin && pinLogin.value === '1' && pinLoginAvailable && pinLoginAvailable.value === '1') {
                setPinModalConfiguration({
                    title: t("login.pin_title"),
                    buttonText: t("login.pin_button"),
                });
                setShowPinLoginModal(true);
            }
        }
    };

    useEffect(() => {
        setLastLoginEmail();
    }, []);

    const handleMsLogin = async (token: string) => {
        const response = await msConnect(token)
            .then(response => {
                const authResult = response.data as AuthResult;
                storeToken(authResult);
                push();
                return true;
            })
            .catch((error) => {
                msLogout();
                void dismissLoading();
                if (error.response?.status === 403 || error.response?.status === 404) {
                    setToast(error.response.data?.message ?? t("common.serverErrorMsg"));
                } else {
                    setToast(t("common.serverErrorMsg"));
                }
                setShowToast(true);
                setLoginStep('email');
                return false;
            });

        if (response === true) {
            await handlePostLogin(() => {
                msLogout();
                setLoginStep('email');
            });
            void dismissLoading();
        }
    };

    useEffect(() => {
        if (loginStep === 'microsoft') {
            void handleMsFlowFromForm();
        }
    }, [loginStep]);

    const handleMsFlowFromForm = async () => {
        // Hide the loader before the popup opens — the popup is its own visual context.
        // Re-show it only after a token is obtained, for the subsequent network calls.
        await dismissLoading();
        const getToken = isPlatform('capacitor') ? getMsToken : getMsTokenWeb;
        const token = await getToken(watchEmail);
        if (!token) { setLoginStep('email'); return; }
        void presentLoading();
        await handleMsLogin(token);
    };

    const handleBackToEmail = () => {
        setLoginStep('email');
    };

    const handleNextStep = async () => {
        if (loginStep !== 'email') return;
        if (!validateEmail(watchEmail)) {
            setToast(t("login.invalidEmail"));
            setShowToast(true);
            return;
        }
        setLoginStep('checking');
        void presentLoading();
        let microsoftFlow = false;
        try {
            const path = await getLoginMethod(watchEmail); // send with :service suffix if present
            // strip :service suffix after API confirms the path
            const cleanEmail = stripServiceSuffix(watchEmail);
            if (cleanEmail !== watchEmail) {
                setValue(FORM_FIELDS.EMAIL, cleanEmail);
            }

            if (path === 'microsoft') {
                microsoftFlow = true;
                setShowMsInfoToast(true);
                setLoginStep('microsoft');
            } else {
                setLoginStep('password');
            }
        } catch (e) {
            setLoginStep('password');
        } finally {
            if (!microsoftFlow) {
                void dismissLoading();
            }
        }
    };

    const handlePostLogin = async (onError?: () => void) => {
        let number = '';
        let terms = null;
        let phone = null;
        let isExact = false;
        let profileResponseData = null;
        let recruitmentHash = null;
        let recruitmentContractFormCompleted = null;
        let recruitmentPathFlowVersion = null;

        const profileResponse = await getProfile(true)
            .then(response => {
                number = response.data.number;
                isExact = response.data.is_coordinator || response.data.is_team_leader || response.data.is_quality;
                terms = response.data.ep2_terms_acceptance;
                phone = response.data.phone;
                profileResponseData = response.data;
                recruitmentHash = response.data.recruitment_hash;
                recruitmentContractFormCompleted = response.data.contract_form_completed;
                recruitmentPathFlowVersion = response.data.recruitment_path_flow_version;

                i18n.changeLanguage(response.data.lang);
                document.documentElement.lang = response.data.lang;
                Preferences.set({'key': 'i18nextLng', 'value': response.data.lang});

                return true;
            })
            .catch(error => {
                logout();
                onError?.();
                if (error.response && error.response.status === 401) {
                    if (error.response.headers['www-authenticate'] && error.response.headers['www-authenticate'].includes('app.security.no_role_account_is_disabled.please_contact_admin')) {
                        setToast(t("app.security.no_role_account_is_disabled.please_contact_admin"));
                    } else if (error.response.data.text === "You should change your password") {
                        setToast(t("login.pleaseChangePassword"));
                    } else {
                        setToast(t("login.disabledAccount"));
                    }
                } else {
                    setToast(t("common.serverErrorMsg"));
                }
                setShowToast(true);
                void dismissLoading();
                return false;
            });

        if (!profileResponse) {
            return;
        }

        await storeProfile(profileResponseData, true);
        const redirect = !isExact ? await getRedirectAfterLogin(number) : null;

        if (recruitmentHash && (recruitmentContractFormCompleted === false || (recruitmentContractFormCompleted === true && recruitmentPathFlowVersion === RecruitmentPathTestVersion.VER_2))) {
            auth.signIn();
            history.push(Links.recruitment + '/' + recruitmentHash);
        } else if (!isExact && !terms) {
            updateShowApprovals(true);
        } else if (!phone && number) {
            updateShowTeamMemberPhone(true);
        } else {
            auth.signIn();
            history.replace(redirect ?? Links.main);
        }
    };

    const onSubmit = async (data: FormFieldsType) => {
        if (loginStep !== 'password' && !data.pin) return;
        void presentLoading()
        const response = await login(data)
                .then(response => {
                    let authResult = response.data as AuthResult;
                    authResult.is_fb_user = false;
                    storeToken(authResult);
					push();
                    return true;
                })
                .catch(error => {
                    if (error.response && error.response.status === 400) {
                        if (data.pin) {
                            setToast(t("login.pinBadCredentialsError"));
                        } else {
                            setToast(t("login.badCredentialsError"));
                        }
                        updateCountFailLogin(countFailLogin + 1);
					} else if (error.response && error.response.status === 500) {
						setToast(t("login.disabledAccount"));
                    } else if (error.response && error.response.status === 403) {
						setToast(t("login.lockedAccount"));
                    } else if (error.response && error.response.status === 429) {
						setToast(t("login.pinTooManyLoginAttendsError"));
                    } else {
                        setToast(t("common.serverErrorMsg"));
                    }
                    setShowToast(true);
                    return false;
                });

            if (data.pin && !response) {
                dispatch(EventType["LOGIN.RESET_PIN"]);
            }

            if (response === true) {
                await handlePostLogin();
            }

        void dismissLoading()
    }

    const pinLogin = async (code: string, email: string) => {
        onSubmit({
            [FORM_FIELDS.EMAIL]: email,
            [FORM_FIELDS.PASSWORD]: code,
            [FORM_FIELDS.PIN]: true,
        })
    }

    return (
        <>
            <Form.Container onSubmit={handleSubmit(onSubmit)}>
                <IonToast
                    isOpen={showToast}
                    onDidDismiss={() => setShowToast(false)}
                    message={toast}
                    duration={6000}
                    position="top"
                    color="danger"
                />
                <IonToast
                    isOpen={showMsInfoToast}
                    onDidDismiss={() => setShowMsInfoToast(false)}
                    message={t("login.redirectingToWorkLogin")}
                    color="success"
                    position="top"
                    duration={4000}
                />
                <StyledInputEmail>
                    <StyledInput name={FORM_FIELDS.EMAIL}
                                 ref={register({
                                     required: true,
                                     validate: value =>
                                         validateEmail(value) || invalidEmailError
                                 })}
                                 placeholder={t("login.inputEmailPlaceholder")}
                                 className={errors.email && 'hasErrors'}
                                 type="text"
                                 readOnly={loginStep !== 'email'}
                                 onClick={(loginStep === 'password' || loginStep === 'microsoft') ? handleBackToEmail : undefined}
                                 style={(loginStep === 'password' || loginStep === 'microsoft') ? {cursor: 'pointer'} : undefined}
                    />
                    {watchEmail && !errors.email && loginStep === 'email' && (
                        <img src={greenCheck} alt=""/>
                    )}
                    {(loginStep === 'password' || loginStep === 'microsoft') && (
                        <img src={EditImage} alt="" onClick={handleBackToEmail} style={{cursor: 'pointer'}}/>
                    )}
                </StyledInputEmail>

                {errors.email && errors.email.message && loginStep === 'email' && <p className="errorMessage">{errors.email.message}</p>}

                {loginStep === 'password' && (
                    <Form.PasswordInput
                        name={FORM_FIELDS.PASSWORD}
                        placeholder={t("login.inputPasswordPlaceholder")}
                        ref={register({
                            required: true
                        })}
                        pinLoginOpen={() => {
                            setPinModalConfiguration({
                                title: t("login.pin_title"),
                                buttonText: t("login.pin_button"),
                            });
                            setShowPinLoginModal(true);
                        }}
                    />
                )}


                {(loginStep === 'email' || loginStep === 'checking') && (
                    <Form.Button
                        type="button"
                        disabled={watchEmail === '' || loginStep === 'checking'}
                        onClick={handleNextStep}
                    >
                        {t("login.nextButton")}
                    </Form.Button>
                )}

                {loginStep === 'password' && (
                    <Form.Button type="submit" disabled={watchPassword === ''}>
                        <img className={"position-left"} src={ExactLoginWhite}/>
                        {t("login.button")}
                    </Form.Button>
                )}
            </Form.Container>

            <AppApprovalsModal
                withLogout={true}
                isOpen={showApprovals}
                onClose={() => updateShowApprovals(false)}
                onSubmit={async () => {
                    updateShowApprovals(false);
                    auth.signIn();
                    let number = await isEmployed();
                    let redirect = await getRedirectAfterLogin(number == '0' ? null : '1');
                    if (redirect) {
                        history.replace(redirect);
                    }
                }}>
            </AppApprovalsModal>

            <TeamMemberPhoneModal
                isOpen={showTeamMemberPhone}
                onClose={() => updateShowTeamMemberPhone(false)}
                onSubmit={async (phone) => {
                    updateShowTeamMemberPhone(false);
                    auth.signIn();
                    await Preferences.set({'key': 'phone', 'value': phone});
                    let number = await isEmployed();
                    let redirect = await getRedirectAfterLogin(number == '0' ? null : '1');
                    if (redirect) {
                        history.replace(redirect);
                    }
                }}>
            </TeamMemberPhoneModal>

            {
                showPinLoginModal && pinModalConfiguration &&
                <PinModal
                    configuration={pinModalConfiguration}
                    isOpen={showPinLoginModal}
                    onSave={(code, email) => {
                        if (email) {
                            pinLogin(code, email)
                        }
                    }}
                    loginWithEmail={true}
                    hiddenValue={true}
                    onClose={() => {
                        setPinModalConfiguration(null)
                        setShowPinLoginModal(false);
                    }}
                />
            }

        </>
    )
}

export default LoginForm;
